import { useCallback, useEffect, useRef, useState } from 'react';
import { loadDiscovery } from '../../app/discovery';
import type { DiscoveryContext } from '../../app/discovery';
import { log } from '../../app/logger';
import { recorder } from '../../app/recorder';
import { recordSurface, SURFACE } from '../../app/resume';
import { readLockNudgeMinB } from '../lockNudge';
import { mergeUndoRows } from './bandB';
import type { UndoRowB } from './bandB';
import { BreakHubSurfaceB } from './BreakHubSurfaceB';
import { CaptureChromeB } from './CaptureChromeB';
import { DiscoverySurfaceB } from './DiscoverySurfaceB';
import { FlagsReviewSurfaceB } from './FlagsReviewSurfaceB';
import { LockedBoardSurfaceB } from './LockedBoardSurfaceB';
import { LockReviewSheetB } from './LockReviewSheetB';
import { SetPieceSheetB } from './SetPieceSheetB';
import { breakSurfaceParamsB, surfaceHintFor, takeoverSurfaceFor } from './surface';
import type { TakeoverSurface } from './surface';

/**
 * B-8 — the **takeover host**: loads the match the chrome renders, re-reads it after every
 * committed transition, records the resume hint on surface entry, and performs the boundary
 * crossing out (design spec §2.2).
 *
 * ── Data is imperative re-read, never a live query ───────────────────────────
 *
 * The engine convention (baseline §7): `useEffect → await load*() → setState`, re-read after
 * every successful write. `loadDiscovery` is the one existing read that returns the match row
 * plus the opponent's *name* in a single readonly transaction — reused as-is; the rows it also
 * returns are B-9's to render and cost one indexed read here. `onTransitioned` → reload is the
 * only re-read trigger a single-writer app needs: `Match.state` cannot move except through this
 * page's own bus.
 *
 * ── The middle band: discovery (B-9) and the board + lock review (B-10) live ─
 *
 * B-8 owns the bands and the switching; B-9/B-10/B-14 own the surfaces. Discovery, the locked
 * board, the lock review, and the break hub (B-14) are all live.
 * "Taps before kickoff create nothing and write nothing" is
 * carried the way A carries it: the rows/tiles render pre-kickoff and decline every contact at
 * `pointerdown` (`canCapture` — the engine deliberately has no state guard on `Increment`).
 *
 * ── Three pieces of NAVIGATION state, layered on the state-driven default ────
 *
 * `takeoverSurfaceFor` stays the total default (its module note reserves exactly this seam):
 *
 * - **`workflowOpen`** — B-31/§21.2. It lived in `BreakHubSurfaceB` until 2026-08-18, which is
 *   the component every one of those navigations UNMOUNTS, so a trip to flags review or the lock
 *   review threw it away and returned the user to the hub's default view. He read that as his
 *   completed analysis being gone and abandoned the match to get back to it. React keeps parent
 *   state across a child unmount, so holding the flag here makes every one of those paths
 *   non-destructive at a stroke, with no persistence involved. It is the ONLY piece of workflow
 *   state a remount loses that the card row cannot rebuild — the *step* is derived from the row
 *   (`useWorkflowB`), which is why the fix is this small.
 * - **`reviewOpenOver`** — the lock review, opened by the strip's lock-nudge action, rendered
 *   as the middle band while the default surface is `discovery` OR `breakHub` (B-14: `canLock`
 *   permits the break, and LOCK-1's nudge renders there — the action must answer). It is a
 *   middle-band surface and NOT a dialog (§2.4 lists it beside the capture surfaces): the
 *   whistle stays reachable, and if a transition commits while reviewing, the state-driven
 *   default simply wins — the effect below drops the flag whenever the surface moves off the
 *   one it opened over (the B-13 opened-over shape).
 * - **`visiting`** — scenario 12/LOCK-5's "visit Discovery from the board": navigation, never
 *   a phase change (there is no `locked → discovery` transition to dispatch). Rendered while
 *   the default surface is `board`; reset the same way. The visit renders B-9's surface over
 *   the shell's own rows read — the add-pattern affordance is absent there by its own
 *   `capturePhase` gate, and returning re-renders the board in INV-11's order (the visit
 *   changed no position, which is INV-4 by op-set type, not a promise).
 */
export interface TakeoverShellProps {
  readonly matchId: string;
  /** §2.2 — fired after `fullTime`'s post-commit render (chrome syncs first); land on the record. */
  readonly onLeftMatch: (matchId: string) => void;
  /** The match row vanished (a cascade elsewhere) — nothing to render; go home. */
  readonly onGone: () => void;
  /**
   * B-31/§21.2 — the adjustments workflow was open when the app was killed, restored from
   * `uiSession.surfaceParams` by `AppB`'s launch resolve. Read once, at mount, exactly as
   * `TabShellB` reads its own `initialOverlay`: every later change is this shell's own.
   */
  readonly initialWorkflowOpen?: boolean;
}

type Load =
  | { readonly phase: 'loading' }
  | {
      readonly phase: 'ready';
      readonly context: DiscoveryContext;
      /**
       * B-19a — LOCK-1's configurable minute, read WITH the match rather than after it. Carrying
       * it in the same `ready` arm is what makes "the strip never evaluates against the default"
       * a structural fact: there is no render of the chrome in which this value is unknown.
       */
      readonly lockNudgeMin: number;
    };

const MODULE = 'ui-b/capture';

/**
 * §21.4's one-line notice. The same sentence the break hub's own audio line uses (`bhub-note-audio`)
 * — one phrase for one fact, so a user who has seen it on the hub reads it as the same thing here.
 */
const VOICE_NOTE_SAVED = 'Voice note saved.';

export function TakeoverShell({
  matchId,
  onLeftMatch,
  onGone,
  initialWorkflowOpen = false,
}: TakeoverShellProps) {
  const [load, setLoad] = useState<Load>({ phase: 'loading' });
  /**
   * B-11 — the undo pill's candidates: `{id, text, lastIncrementTs}` for every hypothesis of
   * this match. The shell's own reads REPLACE the set (a full, authoritative committed read);
   * a live surface's publishes MERGE into it by id (`mergeUndoRows` — the board publishes only
   * its tiles, and a list-only row's open window must survive that). At halftime the break hub
   * publishes nothing and the transition's own reload supplies the fresh set — which is how an
   * increment at 44:58 keeps its pill through the Half whistle (LIVE-6 is bounded by time and
   * `isInMatch`, not by which surface is showing).
   */
  const [undoRows, setUndoRows] = useState<readonly UndoRowB[]>([]);
  /**
   * B-11 — bumped after an undo commits. The live surface re-reads on it (an undo rewrites
   * `lastIncrementTs` to the next-newest increment and can clear `firstObservationCreatedAt`
   * — DISC-4 sort keys no surface can recompute without the ledger; A re-reads for the same
   * reason) and republishes, which retargets or clears the pill.
   */
  const [refreshEpoch, setRefreshEpoch] = useState(0);
  /** One in-flight read at a time; a reload issued mid-read wins by re-running after it. */
  const reading = useRef(false);
  const again = useRef(false);

  const reload = useCallback(async (): Promise<void> => {
    if (reading.current) {
      again.current = true;
      return;
    }
    reading.current = true;
    try {
      do {
        again.current = false;
        /**
         * B-19a — the two reads are concurrent and only ONE of them can eject the user. A match
         * that has vanished is a navigation (below); a settings read that failed is not, so
         * `readLockNudgeMinB` never rejects and answers with the default instead. A lock-nudge
         * minute must not be able to cost somebody the match they are watching.
         */
        const [context, lockNudgeMin] = await Promise.all([
          loadDiscovery(matchId),
          readLockNudgeMinB(),
        ]);
        if (context === undefined) {
          // A vanished match is a normal outcome after a cascade — navigate, don't render.
          onGone();
          return;
        }
        setLoad({ phase: 'ready', context, lockNudgeMin });
        setUndoRows(context.rows);
      } while (again.current);
    } catch (cause) {
      log.exception(MODULE, 'SES-3: the takeover could not read its match', cause);
      onGone();
    } finally {
      reading.current = false;
    }
  }, [matchId, onGone]);

  /** A surface's post-commit fold, folded into the pill's candidates (see `undoRows`). */
  const publishRows = useCallback((rows: readonly UndoRowB[]): void => {
    setUndoRows((previous) => mergeUndoRows(previous, rows));
  }, []);

  /** B-11 — an undo committed: the live surface re-reads (epoch), and so does the shell (the
   *  break hub has no surface to publish; the shell's read is the pill's freshness there). */
  const onUndone = useCallback((): void => {
    setRefreshEpoch((epoch) => epoch + 1);
    void reload();
  }, [reload]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const surface: TakeoverSurface | null =
    load.phase === 'ready' ? takeoverSurfaceFor(load.context.match) : null;

  // ── B-10's navigation state (see the header) ───────────────────────────────
  /**
   * The lock review, in the B-13 opened-over shape (B-14 widened it from a `discovery`-only
   * boolean): LOCK-1's nudge is a MUST across the in-match states and `canLock` permits the
   * break (`machine.ts` — `preMatch ∨ isInMatch`; A's hub offered "Lock my 8" at halftime for
   * the same reason), so the strip's action must answer on the break hub too — a strip action
   * that silently does nothing is a dead control. Remembering the surface it opened OVER keeps
   * the reset rule exact: a committed transition moves the default surface and drops the
   * review (the state-driven default wins every disagreement with navigation).
   */
  const [reviewOpenOver, setReviewOpenOver] = useState<TakeoverSurface | null>(null);
  const [visiting, setVisiting] = useState(false);

  /**
   * B-13 — the flags review, opened from the bottom band's flank slot over ANY surface (§2.4:
   * "reachable from the bottom band during any dead spell" — the band renders on every takeover
   * surface, pre-kickoff and halftime included, so the review does too; pre-kickoff it renders
   * its honest empty state). Navigation state in the B-10 shape: the surface it was opened OVER
   * is remembered, and the state-driven default winning a disagreement is the reset rule — a
   * committed transition (Half, FullTime) moves the default surface and drops the review, so
   * the whistle is never fenced behind a review pane and the new state presents itself.
   *
   * Closing goes read-then-show (the B-10 visit's ordering): a promotion committed a new
   * hypothesis row, and the capture surface remounting under the review seeds its rows from the
   * shell's context at mount — a close that re-rendered before the reload would show a list
   * missing the pattern the user just created.
   */
  const [flagsOpenOver, setFlagsOpenOver] = useState<TakeoverSurface | null>(null);
  const closeFlagsReview = useCallback((): void => {
    void reload().then(() => {
      setFlagsOpenOver(null);
    });
  }, [reload]);

  /**
   * B-12 — the set-piece sheet, opened from the bottom band's flank slot, rendered as a
   * non-modal `<dialog>` over the middle band (§2.4: the board is undisturbed BEHIND it — the
   * surface stays mounted; the whistle, flag and undo pill stay live around it). Deliberately
   * NOT reset on a state change: `CloseSetPiece` is un-state-gated because "a half may end with
   * the sheet open" — the sheet rides through Half/Kickoff2H and unmounts with the shell at the
   * hand-off (an unfinished draft persists, which is SES-3's crash-safety, not a leak).
   */
  const [setPieceOpen, setSetPieceOpen] = useState(false);

  /**
   * B-31/§21.2 — the adjustments workflow's open flag, hoisted out of `BreakHubSurfaceB` (see the
   * header). The hub renders exactly as before; it is told whether the workflow is open and asks
   * this shell to change it.
   */
  const [workflowOpen, setWorkflowOpen] = useState(initialWorkflowOpen);
  /**
   * §21.4 — the one thing the workflow's empty bottom band has to say for itself. A live recording
   * has no stop control once the band goes (its only one is the Flag button), so entering the
   * workflow finalizes it; this line is how the user learns that happened. `null` whenever nothing
   * was running, which is most of the time.
   */
  const [workflowNotice, setWorkflowNotice] = useState<string | null>(null);
  /**
   * B-35/§21.5/R-7 — is the finished card the thing on screen? Reported UP by the workflow, which
   * is the only place the answer lives (its step), and read DOWN by the strip, which withholds the
   * service-worker `update` notice while it is true.
   *
   * **Not derived from `workflowOpen`** — that is true for all four steps — and **not derived from
   * the card row's status** either: a `final` row is what makes the workflow *open* on the card, not
   * what keeps it there, and the same row is on screen as *reference* in the full-time flow. The
   * fact is "which step is rendering", so it is read from the step.
   *
   * It clears on the workflow's unmount, which is the right answer for every one of them: the trip
   * to the lock review (BH-1, which keeps `workflowOpen` true) is not a card being read aloud, and
   * coming back re-reports it. That makes the flag self-healing rather than something to tear down.
   */
  const [cardShowing, setCardShowing] = useState(false);

  /**
   * §21.7 — **the workflow's entry action: one action, two clean-ups**, because they are the same
   * fact — the workflow owns the middle band alone.
   *
   * 1. The set-piece sheet closes. It is a non-modal dialog rendered as a SIBLING of the middle
   *    band, so without this it rides over the workflow and over the finished card.
   * 2. Any in-progress recording is finalized through the engine's existing `finalize` — no engine
   *    change and no new `FinalizeReason`; `userStop` is the truth (a deliberate press took it).
   *    The captured audio survives, which is the engine's own guarantee. The alternative is an
   *    unstoppable, unindicated recording running behind a card the user is reading to a team.
   */
  const openWorkflow = useCallback((): void => {
    setWorkflowOpen(true);
    setSetPieceOpen(false);
    void recorder.finalize('userStop').then(
      (outcome) => {
        if (outcome !== null) setWorkflowNotice(VOICE_NOTE_SAVED);
      },
      (cause: unknown) => {
        // The recording is the engine's to keep; a failed finalize costs the notice, never the
        // workflow the user just asked for.
        log.exception(MODULE, 'AUD-3: the recording could not be finalized on workflow entry', cause);
      },
    );
  }, []);

  /** A DELIBERATE back-out is honoured (§21.2's escapability); an unmount is not. */
  const closeWorkflow = useCallback((): void => {
    setWorkflowOpen(false);
    setWorkflowNotice(null);
  }, []);

  /**
   * §21.7 — the other two `setPieceOpen` closes, keyed on the overlay flags **becoming non-null**,
   * which is what an opener IS. Deliberately not a guard in the reset effect below: that effect's
   * guards are all *"drop this if `surface` no longer matches"*, and neither of these transitions
   * changes `surface` (flags review sets `flagsOpenOver`; the lock review sets `reviewOpenOver`;
   * both leave the surface at its state-driven value) — so a `surface`-keyed guard would fire for
   * NEITHER case. Worse, one that did key on `surface` would close the sheet at Half/Kickoff2H and
   * silently revert this file's own explicit decision above that a half may end with the sheet
   * open. An effect keyed on the flags themselves has the opposite failure: it would also fire
   * when the user opens the sheet from the band *while* a review is showing, flashing the sheet
   * open and shut. An action is the edge, so the action is where the close belongs.
   */
  const openFlagsReview = useCallback((): void => {
    setFlagsOpenOver((open) => open ?? surface);
    setSetPieceOpen(false);
  }, [surface]);

  /** The state-driven default always wins a disagreement: navigation resets when it moves. */
  useEffect(() => {
    if (reviewOpenOver !== null && surface !== reviewOpenOver) setReviewOpenOver(null);
    if (surface !== 'board' && visiting) setVisiting(false);
    if (flagsOpenOver !== null && surface !== flagsOpenOver) setFlagsOpenOver(null);
    /**
     * §21.2's obligation 2 and §21.6 — the same doctrine, now governing the hoisted flag. A
     * `workflowOpen` that outlived the break would be a zombie the hub could never clear, because
     * the hub no longer owns it: at the second-half whistle `takeoverSurfaceFor` returns board or
     * discovery in the same commit, and this drops the flag with `reviewOpenOver` and
     * `flagsOpenOver`. Nothing about the break is lost — a `halftime` draft's window includes
     * `fullTime`, so it is finishable from the match record, and the Needs-finishing queue carries
     * it. `setPieceOpen` is deliberately absent from this effect (see `openFlagsReview`).
     *
     * **`surface !== null` is load-bearing and is what the hoist nearly broke.** `null` means the
     * match row has not been read yet — the state-driven default is *unknown*, not *"not the
     * break"* — and this is the FIRST piece of navigation state that can be `true` before the load
     * resolves, because §21.2 restores it from `surfaceParams` at mount. Without the guard, every
     * relaunch-into-the-workflow cleared the restored flag on its first render and landed the user
     * back on the hub's default view: the exact defect this unit exists to fix, reintroduced by
     * its own fix. (The other three cannot reach that state — they start null/false and are only
     * ever set by a handler on an already-rendered surface — so they are deliberately left alone
     * rather than "made consistent".) Caught by `verify-b-break`'s kill/relaunch check.
     */
    if (workflowOpen && surface !== null && surface !== 'breakHub') {
      setWorkflowOpen(false);
      setWorkflowNotice(null);
    }
  }, [surface, reviewOpenOver, visiting, flagsOpenOver, workflowOpen]);

  /**
   * SES-3 — the resume hint, written on surface entry (§2.2). Fire-and-forget: the hint is a
   * hint (`resolveResume` falls back to the state's default surface without it), so a failed
   * write costs a fallback, never a block. `left` records nothing — `activeMatch` is already
   * cleared, and no hint can bring a relaunch back into the takeover. A Discovery visit records
   * `discovery` (it IS the surface on screen); a relaunch still lands on the board — the
   * engine's own default for a locked match — which is the ephemerality LOCK-5's navigation
   * reading implies.
   *
   * ── B-31/§21.2 — and it is the ONE writer, which is an obligation, not a tidiness ──
   *
   * Since B-31 this effect carries `surfaceParams` as well as the surface. `writeUiSession` is a
   * whole-row `put`, and this effect's deps change on every `reload()`, so a second writer calling
   * the 2-argument form would **erase** the param on the very next committed transition. There is
   * exactly one `recordSurface` call inside the takeover and this is it; the only other B callers
   * are `useSetup`'s, which run before this shell exists.
   */
  useEffect(() => {
    if (load.phase !== 'ready' || surface === null) return;
    const hint =
      visiting && surface === 'board'
        ? SURFACE.discovery
        : surfaceHintFor(surface, load.context.match);
    if (hint === null) return;
    recordSurface(matchId, hint, breakSurfaceParamsB(hint, workflowOpen)).catch((cause: unknown) => {
      log.exception(MODULE, 'SES-3: the resume hint could not be recorded', cause);
    });
  }, [load, surface, visiting, matchId, workflowOpen]);

  if (load.phase === 'loading') {
    return (
      <p className="capb-loading" data-testid="takeover-loading">
        Opening the match…
      </p>
    );
  }

  return (
    <CaptureChromeB
      match={load.context.match}
      opponent={load.context.opponent}
      lockNudgeMin={load.lockNudgeMin}
      onTransitioned={() => {
        void reload();
      }}
      onLeftMatch={onLeftMatch}
      onLockNudge={() => {
        // The nudge only renders while `capturePhase` is `discovery` and the match is in-match
        // (LOCK-1's own predicate), so the surface here is the discovery surface or the break
        // hub — both places the lock review answers.
        if (surface === 'discovery' || surface === 'breakHub') {
          setReviewOpenOver((open) => open ?? surface);
          // §21.7's second overlay-keyed close — the review is about to take the middle band.
          setSetPieceOpen(false);
        }
      }}
      undoRows={undoRows}
      onUndone={onUndone}
      onSetPiece={() => {
        setSetPieceOpen(true);
      }}
      onFlagsReview={openFlagsReview}
      /**
       * §21.4 — **ruled by the stakeholder**: during the adjustments workflow, every step
       * including the card view, the bottom band renders NOTHING. Not the set-piece, flag,
       * flags-review or overflow controls, and not the undo pill. *"If I accidentally tap out"*
       * names accidental taps as the expected case for a phone held up in front of twelve girls,
       * and the safest control is one that is not there. The absence is designed, not guarded —
       * a per-control predicate would be four chances to be wrong instead of one.
       *
       * **"All steps including the card" is load-bearing, not tidiness**: it is what makes a
       * mid-scroll band toggle unreachable. The geometry changes exactly once, at mount, when the
       * workflow surface is already at `scrollTop: 0`.
       *
       * **And it keys on `workflowOpen`, not on "the workflow is the thing rendering right now"**
       * — a boundary §21.4 does not name, decided here and recorded rather than left implicit. If
       * the user steps from the workflow into the lock review (the strip's action, which keeps
       * `workflowOpen` true so BH-1 can put them back), the band stays away for that excursion
       * too. The alternative — restoring it whenever something else holds the middle band — makes
       * the chrome grow and shrink *mid-task*, which is the one thing the geometry contract and
       * skill §5 both exist to prevent, and it would put the flags-review control back within
       * thumb reach of the very episode §21.4 removes it from. The band returns when the user
       * leaves the workflow deliberately, or when the state-driven default takes it away.
       */
      showBottomBand={!workflowOpen}
      /* B-35/R-7 — see `cardShowing` above; the strip withholds the update banner while it is true. */
      cardShowing={cardShowing}
    >
      {renderSurface(surface, load.context)}
      {setPieceOpen && (
        <SetPieceSheetB
          match={load.context.match}
          onClose={() => {
            setSetPieceOpen(false);
          }}
        />
      )}
    </CaptureChromeB>
  );

  function renderSurface(current: TakeoverSurface | null, context: DiscoveryContext) {
    // B-13 — the flags review takes the middle band while open (a middle-band surface, never a
    // dialog: the whistle and the whole bottom band stay live around it). The reset effect above
    // guarantees `current === flagsOpenOver` here, so a committed transition has already dropped
    // the review before this renders the new state's surface.
    if (flagsOpenOver !== null && current !== null && current !== 'left') {
      return <FlagsReviewSurfaceB match={context.match} onClose={closeFlagsReview} />;
    }
    switch (current) {
      case 'preKickoff':
        // §2.4's pre-kickoff surface: the same surface the match will start on, with every
        // contact declined by the `canCapture` gate (the header's negative, carried explicitly
        // by the rows/tiles). A locked pre-match board can only arrive from imported data —
        // B's own setup always starts discovery (scenario 6) — but the preview renders it all
        // the same, with no Discovery link: pre-kickoff there is nothing to visit for.
        if (context.match.capturePhase === 'locked') {
          return (
            <LockedBoardSurfaceB
              match={context.match}
              onRowsChanged={publishRows}
              refreshEpoch={refreshEpoch}
            />
          );
        }
        return (
          <DiscoverySurfaceB
            match={context.match}
            initialRows={context.rows}
            onRowsChanged={publishRows}
            refreshEpoch={refreshEpoch}
          />
        );
      case 'discovery':
        // B-10 — the lock review takes the middle band while open; the chrome (whistle, strip)
        // stays live around it. Closing or locking returns/advances the band.
        if (reviewOpenOver === 'discovery') {
          return (
            <LockReviewSheetB
              matchId={context.match.id}
              onClose={() => {
                setReviewOpenOver(null);
              }}
              onLocked={() => {
                // LOCK-4 committed: the row's capturePhase is now `locked`. Re-read — the
                // state-driven default flips this band to the board, and the reset effect
                // drops the review flag.
                setReviewOpenOver(null);
                void reload();
              }}
            />
          );
        }
        // B-9 — the live surface. Same element position and type as the preview arm above, so
        // React keeps the surface's state (page, rendered order) across the kickoff commit.
        return (
          <DiscoverySurfaceB
            match={context.match}
            initialRows={context.rows}
            onRowsChanged={publishRows}
            refreshEpoch={refreshEpoch}
          />
        );
      case 'board':
        // B-10 — scenario 12/LOCK-5's visit: B-9's surface rendered over the shell's own rows
        // read, with the one control the board owns — the way back.
        if (visiting) {
          return (
            <div className="boardb-visitwrap" data-testid="boardb-visit">
              <div className="boardb-visitbar">
                <button
                  type="button"
                  className="boardb-visit"
                  data-testid="boardb-to-board"
                  onClick={() => {
                    setVisiting(false);
                  }}
                >
                  Back to board
                </button>
                <span className="boardb-visitbar__note">Visiting Discovery — the board is unchanged.</span>
              </div>
              <DiscoverySurfaceB
                match={context.match}
                initialRows={context.rows}
                onRowsChanged={publishRows}
                refreshEpoch={refreshEpoch}
              />
            </div>
          );
        }
        return (
          <LockedBoardSurfaceB
            match={context.match}
            onRowsChanged={publishRows}
            refreshEpoch={refreshEpoch}
            onVisitDiscovery={() => {
              // Re-read BEFORE showing the visit: the visit's rows seed B-9's surface once at
              // mount, and counts recorded on the board since the shell's last read would
              // otherwise render stale. The board's own freshness on return needs no such
              // step — leaving the visit remounts it onto a fresh `loadLockedBoard` read.
              void reload().then(() => {
                setVisiting(true);
              });
            }}
          />
        );
      case 'breakHub':
        // B-14 — the lock review answers the strip's nudge at the break too (`canLock` permits
        // halftime; A's hub offered exactly this). Rendered INSTEAD of the hub, which remounts
        // fresh on close/lock — its own read then sees the new `capturePhase`.
        if (reviewOpenOver === 'breakHub') {
          return (
            <LockReviewSheetB
              matchId={context.match.id}
              onClose={() => {
                setReviewOpenOver(null);
              }}
              onLocked={() => {
                setReviewOpenOver(null);
                void reload();
              }}
            />
          );
        }
        // B-14 — presented from `Match.state`, never navigated to. The hub publishes no undo
        // rows (the transition's own reload supplied the fresh set — see `undoRows` above) and
        // re-reads on the epoch so an undo from the pill moves its manage counts. Its flags
        // entry is B-13's second entry point: the same shell handler the band uses, so the
        // review opens over `breakHub` and the reset rule already covers it.
        return (
          <BreakHubSurfaceB
            match={context.match}
            refreshEpoch={refreshEpoch}
            onFlagsReview={openFlagsReview}
            /* B-31/§21.2 — the flag lives here now; the hub renders from it and asks for changes. */
            workflowOpen={workflowOpen}
            workflowNotice={workflowNotice}
            onOpenWorkflow={openWorkflow}
            onCloseWorkflow={closeWorkflow}
            /* B-35/R-7 — the workflow reports which step is rendering; the strip reads it. */
            onCardShowing={setCardShowing}
          />
        );
      // `left` renders the last surface's nothing for one commit: the chrome's hand-off effect
      // has already fired and the host is about to unmount this shell.
      case 'left':
      case null:
        return null;
    }
  }
}
